import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";

import { useLocation } from "react-router-dom";

const normalizeText = (text) => {
  return text
    .toLocaleLowerCase("en-US")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};
const getColorValue = (options) => {
  if (!Array.isArray(options)) return "";
  for (let opt of options) {
    const name = normalizeText(opt.name);
    if (name.includes("color") || name.includes("renk")) {
      return opt.value?.trim() || "-";
    }
  }
  return "-";
};

const getOptionValue = (options, keywords) => {
  if (!Array.isArray(options)) return "";
  const normalized = (text) =>
    text
      .toLocaleLowerCase("en-US")
      .replace(/ı/g, "i")
      .replace(/İ/g, "i")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  const match = options.find((opt) =>
    keywords.some((keyword) => normalized(opt.name) === normalized(keyword))
  );
  return match?.value || "-";
};

const getSizeValue = (options) => {
  if (!Array.isArray(options)) return "";
  for (let opt of options) {
    const name = normalizeText(opt.name);
    const value = opt.value?.trim() || "";
    if (
      name.includes("size") ||
      name.includes("sıze") ||
      name.includes("beden") ||
      value.includes(" - ") // örn: "Sweatshirt - L"
    ) {
      return value;
    }
  }
  return "-";
};

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const ordersList = location.state?.orders || []; // MyOrdersPage'den gönderilen siparişler
  const selectedShop = location.state?.selectedShop;

  const currentIndex = ordersList.findIndex(
    (o) => String(o.orderId) === String(orderId)
  );

  const prevOrderId =
    currentIndex > 0 ? ordersList[currentIndex - 1]?.orderId : null;
  const nextOrderId =
    currentIndex >= 0 && currentIndex < ordersList.length - 1
      ? ordersList[currentIndex + 1]?.orderId
      : null;

  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);
  const [editOptionState, setEditOptionState] = useState({});
  const [editingAddress, setEditingAddress] = useState(false);
  const [shipToDraft, setShipToDraft] = useState({});
  const [tags, setTags] = useState([]);
  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_shipstation_tags.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        setTags(res.data.tags || []);
      })
      .catch((err) => {
        console.error("Error fetching tags:", err);
        setTags([]);
      });
  }, []);

  useEffect(() => {
    axios
      .get(
        `${settings.apiURL}/get_shipstation_order_details.php?orderId=${orderId}`,
        { headers: authHeader() }
      )
      .then((res) => {
        setOrder(res.data.order);
        setEditNotes({
          internalNote: res.data.order.internalNotes || "",
          customerNotes: res.data.order.customerNotes || "",
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [orderId]);
  const normalizeText = (text) => {
    return text
      .toLocaleLowerCase("en-US")
      .replace(/ı/g, "i")
      .replace(/İ/g, "i")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const handleNoteChange = (field, value) => {
    setEditNotes((prev) => ({ ...prev, [field]: value }));
    setNotesChanged(true);
  };
  const startEditingAddress = () => {
    setShipToDraft(order.shipTo || {});
    setEditingAddress(true);
  };

  const cancelEditingAddress = () => {
    setEditingAddress(false);
  };

  const handleAddressChange = (field, value) => {
    setShipToDraft((prev) => ({ ...prev, [field]: value }));
  };

  const saveAddress = () => {
    axios
      .post(
        `${settings.apiURL}/update_shipping_address.php`,
        { orderId: order.orderId, shipTo: shipToDraft },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast("Address updated!", "success");
          setOrder((prev) => ({ ...prev, shipTo: shipToDraft }));
          setEditingAddress(false);
        } else {
          toast("Failed to update address", "error");
        }
      })
      .catch(() => {
        toast("Error updating address", "error");
      });
  };

  const saveNotes = () => {
    if (!notesChanged) return;
    setSaving(true);
    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId, ...editNotes },
        { headers: authHeader() }
      )
      .then(() => {
        setSaving(false);
        setNotesChanged(false);
        toast("Notes updated successfully!", "success");
      })
      .catch(() => {
        setSaving(false);
        toast("Failed to update notes!", "error");
      });
  };

  const handleSaveOption = (
    orderId,
    lineItemKey,
    optionName,
    newValue,
    key
  ) => {
    axios
      .post(
        `${settings.apiURL}/update_item_options.php`,
        {
          orderId,
          lineItemKey,
          optionName,
          newValue,
        },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast("Option updated successfully!", "success");
          setEditOptionState((prev) => ({
            ...prev,
            [key]: { editing: false, value: newValue },
          }));

          // ✅ Frontend'de anında güncelle
          setOrder((prevOrder) => {
            const updatedItems = prevOrder.items.map((item) => {
              if (item.lineItemKey === lineItemKey) {
                const newOptions = item.options.map((opt) => {
                  if (opt.name.toLowerCase() === optionName.toLowerCase()) {
                    return { ...opt, value: newValue };
                  }
                  return opt;
                });
                return { ...item, options: newOptions };
              }
              return item;
            });
            return { ...prevOrder, items: updatedItems };
          });
        } else {
          toast(res.data.message || "Update failed!", "error");
        }
      })
      .catch((err) => {
        console.error(err);
        toast("Update failed!", "error");
      });
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-US") : "-";

  if (loading) return <p>Loading...</p>;
  if (!order) return <p>Order not found.</p>;

  const itemsFiltered =
    order.items?.filter(
      (item) => !["discount", "shipping"].includes(item.name.toLowerCase())
    ) || [];
  const discountItem = order.items?.find(
    (item) => item.name.toLowerCase() === "discount"
  );
  const discountAmount = discountItem
    ? -Math.abs(discountItem.unitPrice * discountItem.quantity)
    : 0;

  const sectionStyle = {
    border: "1px solid #ccc",
    padding: "15px",
    marginBottom: "15px",
    borderRadius: "5px",
  };
  const tableStyle = {
    width: "100%",
    fontSize: "13px",
    borderCollapse: "collapse",
  };
  const cellStyle = { border: "1px solid #ccc", padding: "8px" };
  const thumbnailStyle = {
    width: "50px",
    transition: "transform 0.3s ease",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        padding: "20px",
        fontSize: "14px",
        position: "relative",
      }}
    >
      <button
        onClick={() => navigate("/admin-orders", { state: { selectedShop } })}
        style={{
          position: "absolute",
          top: "10px",
          right: "20px",
          fontSize: "20px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        ❌
      </button>

      <aside style={{ flex: "0 0 300px" }}>
        <h4>Order Summary</h4>
        <p>
          <strong>Order #</strong>
          <br />
          {order.orderNumber}
        </p>
        <p>
          <strong>Status</strong>
          <br />
          {order.orderStatus || "-"}
        </p>
        <p>
          <strong>Sold To</strong>
          <br />
          {order.shipTo?.name}
          <br />
          {order.customerEmail || "-"}
        </p>
        <p>
          <strong>Ship To Address</strong>
        </p>

        {editingAddress ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <input
              value={shipToDraft.name || ""}
              onChange={(e) => handleAddressChange("name", e.target.value)}
              placeholder="Name"
            />
            <input
              value={shipToDraft.street1 || ""}
              onChange={(e) => handleAddressChange("street1", e.target.value)}
              placeholder="Street"
            />
            <input
              value={shipToDraft.city || ""}
              onChange={(e) => handleAddressChange("city", e.target.value)}
              placeholder="City"
            />
            <input
              value={shipToDraft.state || ""}
              onChange={(e) => handleAddressChange("state", e.target.value)}
              placeholder="State"
            />
            <input
              value={shipToDraft.postalCode || ""}
              onChange={(e) =>
                handleAddressChange("postalCode", e.target.value)
              }
              placeholder="Postal Code"
            />
            <input
              value={shipToDraft.country || ""}
              onChange={(e) => handleAddressChange("country", e.target.value)}
              placeholder="Country"
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button onClick={saveAddress} className="btn btn-success btn-sm">
                Save
              </button>
              <button
                onClick={cancelEditingAddress}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {order.shipTo?.name}
            <br />
            {order.shipTo?.street1}
            <br />
            {order.shipTo?.city}, {order.shipTo?.state}{" "}
            {order.shipTo?.postalCode}
            <br />
            {order.shipTo?.country}
            <br />
            <button
              onClick={startEditingAddress}
              className="btn btn-link p-0 mt-1"
            >
              Edit Address
            </button>
          </div>
        )}

        <p>
          <strong>Product:</strong> $
          {itemsFiltered
            .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
            .toFixed(2)}
        </p>
        <p>
          <strong>Shipping:</strong> ${order.shippingAmount}
        </p>
        <p>
          <strong>Tax:</strong> ${order.taxAmount}
        </p>
        <p>
          <strong>Order Total:</strong> ${order.orderTotal}
        </p>
        <p>
          <strong>Discount Total:</strong>{" "}
          {discountAmount !== 0 ? `$${discountAmount.toFixed(2)}` : "-$0.00"}
        </p>
        <p>
          <strong>Total Paid:</strong> ${order.amountPaid}
        </p>
        <p>
          <strong>Order Date:</strong> {formatDate(order.createDate)}
        </p>
        <p>
          <strong>Ship By:</strong> {formatDate(order.shipByDate)}
        </p>
        <p>
          <strong>Order Items:</strong> {itemsFiltered.length} items
        </p>
      </aside>

      <main style={{ flex: 1 }}>
        <h3>Shipment Details</h3>
        <div
          style={{
            ...sectionStyle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
          }}
        >
          {/* Left: Ship To Address */}
          <div>
            <p>
              <strong>Ship To Address</strong>
              <br />
              {order.shipTo?.name}
              <br />
              {order.shipTo?.street1}
              <br />
              {order.shipTo?.city}, {order.shipTo?.state}{" "}
              {order.shipTo?.postalCode}
              <br />
              {order.shipTo?.country}
            </p>
            <span
              style={{
                backgroundColor:
                  order.shipTo?.addressVerified ===
                  "Address validated successfully"
                    ? "#d4edda"
                    : "#f8d7da",
                color:
                  order.shipTo?.addressVerified ===
                  "Address validated successfully"
                    ? "#155724"
                    : "#721c24",
                padding: "4px 8px",
                borderRadius: "6px",
                display: "inline-block",
                marginTop: "5px",
                fontWeight: "bold",
              }}
            >
              {order.shipTo?.addressVerified ===
              "Address validated successfully"
                ? "✅ Address Validated"
                : "⚠️ Address Not Validated"}
            </span>
          </div>

          {/* Right: Tag Dropdown */}
          <div style={{ minWidth: "180px" }}>
            <label style={{ fontWeight: "bold" }}>Tag:</label>
            <select
              className="form-select form-select-sm"
              value={order.tagIds?.[0] || ""}
              onChange={(e) => {
                const selectedTagId = e.target.value;

                const payload =
                  selectedTagId === ""
                    ? {
                        orderId: order.orderId,
                        tagId: "remove",
                        removeTagId: order.tagIds?.[0],
                      }
                    : {
                        orderId: order.orderId,
                        tagId: selectedTagId,
                      };

                axios
                  .post(`${settings.apiURL}/update_order_tag.php`, payload, {
                    headers: authHeader(),
                  })
                  .then((res) => {
                    if (res.data.success) {
                      toast(res.data.message, "success");
                      setOrder((prevOrder) => ({
                        ...prevOrder,
                        tagIds: selectedTagId === "" ? [] : [selectedTagId],
                      }));
                    } else {
                      toast("Failed to update tag!", "error");
                    }
                  })
                  .catch((err) => {
                    console.error(err);
                    toast("Tag update failed!", "error");
                  });
              }}
            >
              <option value="">No tag</option>
              {tags.map((tag) => (
                <option key={tag.tagId} value={tag.tagId}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={sectionStyle}>
          <h4>Shipment Items</h4>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={cellStyle}>Image</th>
                <th style={cellStyle}>Item Name</th>
                <th style={cellStyle}>SKU</th>
                <th style={cellStyle}>Unit Cost</th>
                <th style={cellStyle}>Quantity</th>
                <th style={cellStyle}>Info</th>
              </tr>
            </thead>
            <tbody>
              {itemsFiltered.map((item, i) => (
                <tr key={i}>
                  <td style={cellStyle}>
                    <img
                      src={item.imageUrl}
                      alt=""
                      style={thumbnailStyle}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.transform = "scale(5)")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    />
                  </td>
                  <td style={cellStyle}>
                    {item.name}
                    <br />
                    {item.options
                      ?.filter((opt) => {
                        const name = normalizeText(opt.name);
                        return name.includes("size") || name.includes("color");
                      })
                      .map((opt, j) => {
                        const nameLower = normalizeText(opt.name);
                        const key = `${i}-${j}`;
                        const isEditing =
                          editOptionState[key]?.editing || false;
                        const tempValue =
                          editOptionState[key]?.value ?? opt.value;

                        return (
                          <div key={j} style={{ marginTop: "5px" }}>
                            <strong>{opt.name}:</strong>{" "}
                            {isEditing ? (
                              <>
                                <input
                                  type="text"
                                  value={tempValue}
                                  onChange={(e) =>
                                    setEditOptionState((prev) => ({
                                      ...prev,
                                      [key]: {
                                        ...prev[key],
                                        value: e.target.value,
                                      },
                                    }))
                                  }
                                  style={{ width: "150px", fontSize: "12px" }}
                                />
                                <button
                                  style={{
                                    marginLeft: "5px",
                                    fontSize: "12px",
                                  }}
                                  onClick={() =>
                                    handleSaveOption(
                                      order.orderId,
                                      item.lineItemKey,
                                      opt.name,
                                      tempValue,
                                      key
                                    )
                                  }
                                >
                                  Save
                                </button>
                              </>
                            ) : (
                              <>
                                {opt.value}
                                <span
                                  style={{
                                    color: "blue",
                                    cursor: "pointer",
                                    marginLeft: "5px",
                                    fontSize: "12px",
                                  }}
                                  onClick={() =>
                                    setEditOptionState((prev) => ({
                                      ...prev,
                                      [key]: {
                                        editing: true,
                                        value: opt.value,
                                      },
                                    }))
                                  }
                                >
                                  Edit
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })}

                    <div>
                      <strong>Personalization:</strong>{" "}
                      {item.options?.find(
                        (opt) => opt.name.toLowerCase() === "personalization"
                      )?.value || "Not requested on this item."}
                      {order.storeName === "0 AMZ" && (
                        <div style={{ marginTop: "5px" }}>
                          <strong>Amazon Order Link:</strong>{" "}
                          <a
                            href={`https://sellercentral.amazon.com/orders-v3/order/${order.orderNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "blue",
                              textDecoration: "underline",
                            }}
                          >
                            View Order
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={cellStyle}>{item.sku}</td>
                  <td style={cellStyle}>${item.unitPrice}</td>
                  <td style={cellStyle}>{item.quantity}</td>
                  <td style={cellStyle}>
                    <button
                      onClick={() => {
                        const shopName = order.storeName || "Shop Name";

                        const customerName = order.shipTo?.name || "Customer";
                        const orderNumber = `#${order.orderNumber}`;
                        const firstName =
                          localStorage.getItem("first_name") || "";
                        const lastName =
                          localStorage.getItem("last_name") || "";
                        const userName =
                          `${firstName} ${lastName}`.trim() || "User";

                        const cleanOptions =
                          item.options?.filter(
                            (opt) =>
                              !normalizeText(opt.name).includes(
                                "personalization"
                              )
                          ) || [];

                        const size = getSizeValue(cleanOptions);
                        const color = getColorValue(cleanOptions);

                        const textToCopy = `${shopName} - ${orderNumber} - ${customerName} - ${userName}
${size} - ${color} - quantity: ${item.quantity}`;

                        navigator.clipboard
                          .writeText(textToCopy)
                          .then(() => toast("Copied to clipboard!", "success"))
                          .catch(() => toast("Copy failed!", "error"));
                      }}
                    >
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={sectionStyle}>
          <h4>Notes</h4>
          <label>Internal Note:</label>
          <textarea
            value={
              editNotes.internalNote === "No notes available"
                ? ""
                : editNotes.internalNote
            }
            onChange={(e) => handleNoteChange("internalNote", e.target.value)}
            style={{ width: "100%", minHeight: "50px" }}
          />
          <label>Customer Note:</label>
          <textarea
            value={
              editNotes.customerNotes === "No notes available"
                ? ""
                : editNotes.customerNotes
            }
            onChange={(e) => handleNoteChange("customerNotes", e.target.value)}
            style={{ width: "100%", minHeight: "50px", marginTop: "10px" }}
          />

          {order.giftMessage && (
            <div
              style={{
                marginTop: "15px",
                padding: "10px",
                background: "#f9f9f9",
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <strong>Gift Note:</strong>
              <p>{order.giftMessage}</p>
            </div>
          )}

          <button
            className="btn btn-primary mt-2"
            onClick={saveNotes}
            disabled={saving}
          >
            Save Notes
          </button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            className="btn"
            style={{
              backgroundColor: prevOrderId ? "#FFA500" : "#ccc",
              color: "white",
              cursor: prevOrderId ? "pointer" : "not-allowed",
              pointerEvents: prevOrderId ? "auto" : "none",
            }}
            onClick={() =>
              navigate(`/order-details/${prevOrderId}`, {
                state: { orders: ordersList, selectedShop }, // ✅ Buraya selectedShop eklendi
              })
            }
          >
            ⬅️ Previous
          </button>

          <button
            className="btn"
            style={{
              backgroundColor: nextOrderId ? "#FFA500" : "#ccc",
              color: "white",
              cursor: nextOrderId ? "pointer" : "not-allowed",
              pointerEvents: nextOrderId ? "auto" : "none",
            }}
            onClick={() =>
              navigate(`/order-details/${nextOrderId}`, {
                state: { orders: ordersList, selectedShop }, // ✅ Buraya selectedShop eklendi
              })
            }
          >
            Next ➡️
          </button>
        </div>
      </main>
    </div>
  );
};

export default OrderDetailsPage;
